import React, { useState, useEffect } from 'react';
import { X, Save, User, MapPin } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { Person } from '../../types';

interface PersonFormModalProps {
  person?: Person | null;
  onClose: () => void;
}

const formatInputPhone = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const stripPhone = (value: string) => {
  return value.replace(/\D/g, '');
};

export const PersonFormModal: React.FC<PersonFormModalProps> = ({ person, onClose }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    organization_id: '',
    affiliation_id: '',
    notes: ''
  });

  const { data: allOrgs } = useQuery({
    queryKey: ['all-organizations-list'],
    queryFn: async () => {
      const { data } = await supabase.from('organizations').select('id, name').order('name');
      return (data || []).map(o => ({ id: o.id, name: o.name }));
    }
  });

  const { data: affiliations } = useQuery({
    queryKey: ['lookup', 'affiliations'],
    queryFn: async () => {
      const { data } = await supabase.from('affiliations').select('id, label').order('label');
      return (data || []).map(a => ({ id: a.id, name: a.label }));
    }
  });

  useEffect(() => {
    if (person) {
      setFormData({
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        email: person.email || '',
        phone: formatInputPhone(person.phone || ''),
        street_address: person.street_address || '',
        city: person.city || '',
        state: person.state || '',
        zip_code: person.zip_code || '',
        organization_id: person.organization_id || '',
        affiliation_id: person.affiliation_id || '',
        notes: (person as any).notes || ''
      });
    }
  }, [person]);

  const upsertPersonMutation = useMutation({
    mutationFn: async (newData: typeof formData) => {
      const payload = {
        first_name: newData.first_name,
        last_name: newData.last_name,
        email: newData.email.toLowerCase().trim() || null,
        phone: stripPhone(newData.phone) || null,
        street_address: newData.street_address || null,
        city: newData.city || null,
        state: newData.state || null,
        zip_code: newData.zip_code || null,
        organization_id: newData.organization_id || null,
        affiliation_id: newData.affiliation_id || null,
        notes: newData.notes || null,
        created_by_email: profile?.email
      };

      if (person) {
        const { error } = await supabase.from('people').update(payload).eq('id', person.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('people').insert([payload]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['person-detail', person?.id] });
      onClose();
    }
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-xl rounded-md shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            <User size={22} className="text-blue-600" />
            {person ? 'Update Profile' : 'New Person'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form 
          className="p-8 space-y-6 overflow-y-auto"
          onSubmit={(e) => {
            e.preventDefault();
            upsertPersonMutation.mutate(formData);
          }}
        >
          <div className="grid grid-cols-2 gap-6">
            <Input label="First Name" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
            <Input label="Last Name" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Email" type="email" placeholder="personnel@hfli.org" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            <Input label="Phone" type="tel" value={formData.phone} onChange={handlePhoneChange} placeholder="555-555-5555" />
          </div>

          <div className="space-y-4 pt-2">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin size={12} /> Location Details
            </h3>
            <Input label="Street Address" value={formData.street_address} onChange={e => setFormData({...formData, street_address: e.target.value})} placeholder="123 Example Way" />
            <div className="grid grid-cols-3 gap-4">
               <div className="col-span-1">
                 <Input label="City" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
               </div>
               <div>
                 <Input label="State" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="MI" />
               </div>
               <div>
                 <Input label="Zip Code" value={formData.zip_code} onChange={e => setFormData({...formData, zip_code: e.target.value})} />
               </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SearchableSelect 
              label="Affiliation"
              options={affiliations || []}
              value={formData.affiliation_id}
              onChange={(val) => setFormData({ ...formData, affiliation_id: val })}
              placeholder="Select Affiliation..."
            />
            <SearchableSelect 
              label="Institutional Link"
              options={allOrgs || []}
              value={formData.organization_id}
              onChange={(val) => setFormData({ ...formData, organization_id: val })}
              placeholder="Select Organization..."
            />
          </div>

          <Input label="Administrative Notes" isTextArea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Internal logging..." />

          <div className="flex gap-4 pt-4">
             <Button type="button" variant="ghost" className="flex-1" onClick={onClose}>Discard</Button>
             <Button type="submit" className="flex-[2] py-4 shadow-lg" isLoading={upsertPersonMutation.isPending} icon={<Save size={18} />}>
               Commit Changes
             </Button>
          </div>
        </form>
      </div>
    </div>
  );
};